import { useAuth0 } from '@auth0/auth0-react'
import { NavLink } from 'react-router-dom'
import type { ReactNode } from 'react'

interface NavItem {
  to: string
  label: string
  icon: string
  end?: boolean
}

const TOP_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: '◈', end: true },
  { to: '/namespaces', label: 'Namespaces', icon: '⬡' },
  { to: '/analytics', label: 'Analytics', icon: '▲' },
]

interface Props {
  children: ReactNode
  /** Optional page title shown in the topbar */
  title?: string
  /** Optional actions rendered in the topbar right slot */
  actions?: ReactNode
}

export function Layout({ children, title, actions }: Props) {
  const { user, logout } = useAuth0()

  return (
    <div style={styles.root}>
      {/* ── Sidebar ── */}
      <aside style={styles.sidebar}>
        {/* Logo */}
        <div style={styles.logoArea}>
          <NavLink to="/" style={styles.logo}>
            <div style={styles.logoMark}>T</div>
            <span>Terra<em style={{ color: 'var(--accent)', fontStyle: 'normal' }}>mantle</em></span>
          </NavLink>
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          <div style={styles.navSection}>
            <div style={styles.navLabel}>// workspace</div>
            {TOP_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                style={({ isActive }) => ({
                  ...styles.navItem,
                  ...(isActive ? styles.navItemActive : {}),
                })}
              >
                <span style={styles.navIcon}>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* User footer */}
        <div style={styles.sidebarFooter}>
          <div style={styles.userRow}>
            <div style={styles.userAvatar}>
              {user?.name?.[0]?.toUpperCase() ?? '?'}
            </div>
            <div style={styles.userInfo}>
              <div style={styles.userName}>{user?.name ?? user?.email ?? 'User'}</div>
              <div style={styles.userEmail}>{user?.email}</div>
            </div>
          </div>
          <button
            style={styles.logoutBtn}
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main style={styles.main}>
        {/* Topbar */}
        {(title || actions) && (
          <header style={styles.topbar}>
            <h1 style={styles.topbarTitle}>{title}</h1>
            {actions && <div style={styles.topbarActions}>{actions}</div>}
          </header>
        )}

        {/* Content */}
        <div style={styles.content}>
          {children}
        </div>
      </main>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
  },
  sidebar: {
    width: 220,
    minWidth: 220,
    background: 'var(--bg2)',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  logoArea: {
    padding: '18px 18px 14px',
    borderBottom: '1px solid var(--border)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: 'var(--head)',
    fontWeight: 800,
    fontSize: '1rem',
    letterSpacing: '-0.02em',
    color: 'var(--text)',
    textDecoration: 'none',
  },
  logoMark: {
    width: 28,
    height: 28,
    borderRadius: 5,
    background: 'linear-gradient(135deg, var(--accent), #c2560a)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    fontWeight: 800,
    color: '#07090d',
    fontFamily: 'var(--head)',
    flexShrink: 0,
  },
  nav: {
    flex: 1,
    overflow: 'auto',
    padding: '12px 0',
  },
  navSection: {
    padding: '0 10px',
  },
  navLabel: {
    fontFamily: 'var(--mono)',
    fontSize: '0.58rem',
    letterSpacing: '0.1em',
    color: 'var(--text3)',
    padding: '6px 8px 4px',
    textTransform: 'uppercase',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 10px',
    borderRadius: 5,
    fontFamily: 'var(--mono)',
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    color: 'var(--text2)',
    textDecoration: 'none',
    transition: 'all 0.15s',
    marginBottom: 2,
  },
  navItemActive: {
    background: 'var(--accent-d)',
    color: 'var(--accent)',
    borderLeft: '2px solid var(--accent)',
    paddingLeft: 8,
  },
  navIcon: {
    fontSize: '0.8rem',
    width: 14,
    textAlign: 'center',
  },
  sidebarFooter: {
    borderTop: '1px solid var(--border)',
    padding: '12px 14px',
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  userAvatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'var(--accent-d)',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--head)',
    fontWeight: 700,
    fontSize: '0.75rem',
    color: 'var(--accent)',
    flexShrink: 0,
  },
  userInfo: { overflow: 'hidden' },
  userName: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--text)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userEmail: {
    fontFamily: 'var(--mono)',
    fontSize: '0.58rem',
    color: 'var(--text3)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  logoutBtn: {
    width: '100%',
    padding: '5px 0',
    background: 'transparent',
    border: '1px solid var(--border2)',
    borderRadius: 4,
    color: 'var(--text2)',
    fontFamily: 'var(--mono)',
    fontSize: '0.6rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 28px',
    height: 56,
    background: 'rgba(7,9,13,0.93)',
    backdropFilter: 'blur(14px)',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
  },
  topbarTitle: {
    fontFamily: 'var(--head)',
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: 'var(--text)',
  },
  topbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '24px 28px',
  },
}
